<script setup lang="ts">
import BackstageIcon from '@/components/common/BackstageIcon.vue';
import BackstageImage from '@/components/common/BackstageImage.vue';
import { useAnalytics } from '@/composables/analytics';
import { GaCustomEvents } from '@/models/ga-custom-events.enum';
import { Icons } from '@/models/icons.enum';
import { nextTick, onMounted, ref } from 'vue';

export interface SubtitleTrack {
  src: string;
  srclang: string;
  label: string;
  default?: boolean;
}

export interface Props {
  src: string;
  poster?: string;
  title?: string;
  fallbackText?: string;
  autoplay?: boolean;
  subtitles?: SubtitleTrack[];
  /**
   * Controls how the video (and poster) fits within its container.
   * - `'cover'` (default): fixed 16/9 aspect-ratio, fills container.
   * - `'contain'`: inherits container dimensions and letterboxes the video.
   */
  objectFit?: 'cover' | 'contain';
}

const props = withDefaults(defineProps<Props>(), {
  autoplay: false,
  objectFit: 'cover',
});

const { addCustomGaEvent } = useAnalytics();

const isPlaying = ref(false);
const videoRef = ref<HTMLVideoElement | null>(null);

async function playVideo() {
  isPlaying.value = true;
  await nextTick();

  try {
    await videoRef.value?.play();
    addCustomGaEvent({
      event: GaCustomEvents.VideoPlay,
      video_title: props.title,
    });
  } catch {
    // Autoplay may be blocked by the browser — silently ignore
    isPlaying.value = false;
  }
}

function pauseVideo() {
  if (videoRef.value) {
    videoRef.value.pause();
  }
}

defineExpose({
  playVideo,
  pauseVideo,
});

onMounted(() => {
  if (props.autoplay) {
    playVideo();
  }
});
</script>

<template>
  <div class="backstage-video-container" :class="`backstage-video-container--${props.objectFit}`" @contextmenu.prevent>
    <video
      v-if="props.src && isPlaying"
      ref="videoRef"
      controlsList="nodownload noremoteplayback"
      controls
      autoplay
      class="backstage-video"
      disablePictureInPicture>
      <source :src="props.src" />
      <track
        v-for="(track, index) in props.subtitles"
        :key="index"
        kind="subtitles"
        :src="track.src"
        :srclang="track.srclang"
        :label="track.label"
        :default="track.default" />
      {{ props.fallbackText ?? 'Your browser does not support the video tag.' }}
    </video>
    <BackstageImage
      v-if="props.src && props.poster && !isPlaying"
      :src="props.poster"
      :alt="props.title ?? 'Video thumbnail'"
      class="backstage-video-poster"
      @click="playVideo" />

    <div v-if="props.src && !isPlaying" class="backstage-video-play-overlay" @click="playVideo">
      <div class="backstage-video-play-button">
        <BackstageIcon :name="Icons.PLAY" />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.backstage-video-container {
  position: relative;
  width: 100%;

  .backstage-video-poster {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
  }

  &--cover {
    aspect-ratio: 16 / 9;

    .backstage-video {
      width: 100%;
      aspect-ratio: 16 / 9;
      border-radius: 6px;
    }

    .backstage-video-poster {
      object-fit: cover;
    }
  }

  &--contain {
    height: 100%;
    aspect-ratio: unset;

    .backstage-video {
      width: 100%;
      height: 100%;
      aspect-ratio: unset;
      object-fit: contain;
      border-radius: 0;
    }

    .backstage-video-poster {
      object-fit: contain;
    }
  }

  .backstage-video-play-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    &:hover {
      background-color: rgb(0, 0, 0, 0.2);
    }

    .backstage-video-play-button {
      width: 48px;
      height: 48px;
      background: var(--primary-bg-color);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: var(--primary-text-color);
      &:hover {
        background-color: rgb(255, 255, 255, 0.8);
      }
    }
  }
}
</style>
